/**
 * Product Feedback Dialog
 *
 * Captures user feedback for enhancing the Product Definition section.
 * Provides product-specific suggestions and enhancement options.
 */

import React, { useState } from 'react';
import {
  X,
  Sparkles,
  RefreshCw,
  MessageSquare,
  Lightbulb,
  AlertCircle,
  ChevronDown,
  Box,
  Layers,
  Shield,
  Users,
  Target,
  TrendingUp,
  Gauge,
  Calendar,
  Wrench,
  ClipboardCheck,
} from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

export interface ProductFeedback {
  userFeedback: string;
  enhancementLevel: 'minor' | 'moderate' | 'significant';
  focusAreas: string[];
}

interface ProductFeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: ProductFeedback) => void;
  productName?: string;
  isSubmitting?: boolean;
}

// ============================================================================
// FOCUS AREA OPTIONS
// ============================================================================

const PRODUCT_FOCUS_AREAS = [
  { id: 'components', label: 'Components & Features', description: 'Add detail to product components and their capabilities', icon: Layers },
  { id: 'technical', label: 'Technical Approach', description: 'Expand methodology, tools, and technology stack', icon: Wrench },
  { id: 'staffing', label: 'Staffing Model', description: 'Improve roles, qualifications, and team structure', icon: Users },
  { id: 'risks', label: 'Risk Assessment', description: 'Enhance risk identification and mitigations', icon: Shield },
  { id: 'implementation', label: 'Implementation Plan', description: 'Detail phases, milestones, and go-live criteria', icon: Calendar },
  { id: 'quality', label: 'Quality Assurance', description: 'Add QA processes, metrics, and standards', icon: ClipboardCheck },
  { id: 'sla', label: 'Service Levels', description: 'Define SLAs, targets, and measurement methods', icon: Gauge },
  { id: 'differentiators', label: 'Differentiators', description: 'Strengthen competitive advantages', icon: TrendingUp },
  { id: 'metrics', label: 'Success Metrics', description: 'Add KPIs and measurable outcomes', icon: Target },
  { id: 'scope', label: 'Scope Definition', description: 'Clarify included/excluded items and assumptions', icon: Box },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function ProductFeedbackDialog({
  isOpen,
  onClose,
  onSubmit,
  productName,
  isSubmitting = false,
}: ProductFeedbackDialogProps) {
  const [feedback, setFeedback] = useState('');
  const [enhancementLevel, setEnhancementLevel] = useState<'minor' | 'moderate' | 'significant'>('moderate');
  const [focusAreas, setFocusAreas] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(true);

  // Quick feedback suggestions for product enhancement
  const suggestions = [
    'Add more detail to the product components',
    'Strengthen the competitive differentiators',
    'Expand the technical approach with specific tools and methodologies',
    'Improve the risk assessment with more specific mitigations',
    'Add more detailed staffing requirements and qualifications',
    'Include more specific service levels and SLAs',
    'Detail the implementation phases with clearer milestones',
    'Add measurable success metrics and KPIs',
    'Clarify the scope with explicit inclusions and exclusions',
    'Enhance quality assurance processes and standards',
    'Improve the bid recommendation rationale',
    'Add innovation highlights and unique value propositions',
  ];

  const toggleFocusArea = (areaId: string) => {
    setFocusAreas(prev =>
      prev.includes(areaId)
        ? prev.filter(id => id !== areaId)
        : [...prev, areaId]
    );
  };

  const handleSubmit = () => {
    if (!feedback.trim() && focusAreas.length === 0) {
      return;
    }

    onSubmit({
      userFeedback: feedback.trim(),
      enhancementLevel,
      focusAreas,
    });
  };

  const handleSuggestionClick = (suggestion: string) => {
    setFeedback(prev => {
      if (prev.trim()) {
        return `${prev.trim()}. ${suggestion}`;
      }
      return suggestion;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-bw-surface dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-bw-border dark:border-gray-700 bg-bw-surface-subtle dark:from-gray-800 dark:to-gray-750 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-bw-primary-tint dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                <Box className="w-5 h-5 text-bw-primary dark:text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-bw-ink dark:text-white">
                  Enhance Product Definition
                </h2>
                <p className="text-sm text-bw-muted dark:text-bw-muted">
                  {productName || 'Improve your product synthesis with AI'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="p-2 text-bw-muted hover:text-bw-muted dark:hover:text-gray-300 hover:bg-bw-surface-subtle dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Focus Areas */}
          <div>
            <label className="block text-sm font-medium text-bw-ink dark:text-gray-300 mb-3">
              <Target className="w-4 h-4 inline mr-2" />
              Enhancement Focus Areas
              <span className="text-bw-muted font-normal ml-2">(Select what to improve)</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {PRODUCT_FOCUS_AREAS.map(area => {
                const Icon = area.icon;
                const isSelected = focusAreas.includes(area.id);
                return (
                  <button
                    key={area.id}
                    type="button"
                    onClick={() => toggleFocusArea(area.id)}
                    disabled={isSubmitting}
                    className={`p-3 rounded-lg border-2 transition-all text-left disabled:opacity-50 ${
                      isSelected
                        ? 'border-purple-500 bg-bw-primary-tint dark:bg-purple-900/20'
                        : 'border-bw-border dark:border-gray-600 hover:border-bw-border dark:hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-bw-primary' : 'text-bw-muted'}`} />
                      <span className={`font-medium text-sm ${
                        isSelected ? 'text-bw-primary dark:text-purple-300' : 'text-bw-ink dark:text-white'
                      }`}>
                        {area.label}
                      </span>
                    </div>
                    <p className="text-xs text-bw-muted dark:text-bw-muted mt-1 ml-6">
                      {area.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Feedback textarea */}
          <div>
            <label className="block text-sm font-medium text-bw-ink dark:text-gray-300 mb-2">
              <MessageSquare className="w-4 h-4 inline mr-2" />
              Additional Instructions
              <span className="text-bw-muted font-normal ml-2">(Optional)</span>
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Tell the AI specific things you'd like to change or improve about the product definition..."
              rows={3}
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-bw-border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white resize-none disabled:opacity-50"
            />
          </div>

          {/* Quick suggestions */}
          <div>
            <button
              type="button"
              onClick={() => setShowSuggestions(!showSuggestions)}
              className="flex items-center gap-2 text-sm font-medium text-bw-ink dark:text-gray-300 mb-2"
            >
              <Lightbulb className="w-4 h-4 text-fit-moderate" />
              Quick Suggestions
              <ChevronDown className={`w-4 h-4 transition-transform ${showSuggestions ? 'rotate-180' : ''}`} />
            </button>
            {showSuggestions && (
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 text-xs bg-bw-surface-subtle dark:bg-gray-700 text-bw-ink dark:text-gray-300 rounded-full hover:bg-bw-primary-tint dark:hover:bg-purple-900/30 hover:text-bw-primary dark:hover:text-purple-300 transition-colors disabled:opacity-50"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Enhancement level */}
          <div>
            <label className="block text-sm font-medium text-bw-ink dark:text-gray-300 mb-3">
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Enhancement Level
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'minor', label: 'Minor', description: 'Polish & refine' },
                { value: 'moderate', label: 'Moderate', description: 'Add detail & depth' },
                { value: 'significant', label: 'Significant', description: 'Major expansion' },
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setEnhancementLevel(option.value as typeof enhancementLevel)}
                  disabled={isSubmitting}
                  className={`p-3 rounded-lg border-2 transition-all text-left disabled:opacity-50 ${
                    enhancementLevel === option.value
                      ? 'border-purple-500 bg-bw-primary-tint dark:bg-purple-900/20'
                      : 'border-bw-border dark:border-gray-600 hover:border-bw-border dark:hover:border-gray-500'
                  }`}
                >
                  <div className={`font-medium text-sm ${
                    enhancementLevel === option.value
                      ? 'text-bw-primary dark:text-purple-300'
                      : 'text-bw-ink dark:text-white'
                  }`}>
                    {option.label}
                  </div>
                  <div className="text-xs text-bw-muted dark:text-bw-muted mt-0.5">
                    {option.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Info note */}
          <div className="flex items-start gap-3 p-3 bg-bw-primary-tint dark:bg-purple-900/20 rounded-lg text-bw-primary dark:text-purple-200 text-sm">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              The AI will enhance your product definition based on your focus areas and feedback.
              This includes improving components, technical approach, staffing, and other selected areas
              while maintaining consistency with your deliverables.
            </div>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="px-6 py-4 bg-bw-surface-subtle dark:bg-gray-800/50 border-t border-bw-border dark:border-gray-700 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-bw-ink dark:text-gray-300 bg-bw-surface dark:bg-gray-700 border border-bw-border dark:border-gray-600 rounded-lg hover:bg-bw-surface-subtle dark:hover:bg-gray-600 transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={(focusAreas.length === 0 && !feedback.trim()) || isSubmitting}
            className="px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Enhancing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Enhance Product
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductFeedbackDialog;
